import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from 'components/common';
import { notificationsService } from 'services';
import { severityMeta, severityToastClass } from 'lib/notifications.mjs';
import { COMMENT_REMINDER_PATH } from 'lib/commentReminder.mjs';
import { useAuth } from './AuthContext.jsx';

/**
 * The unread badge, the toast for anything that just arrived, and the one place
 * that knows how many there are.
 *
 * ── WHY THE COUNT LIVES IN A CONTEXT AND THE LIST DOES NOT ───────────────────
 * The badge is rendered by both shells, on every page. The list is rendered by
 * one view. Putting the count here means the bell can show it everywhere without
 * the notification view having to be mounted, and it means marking something as
 * read in the view updates the bell without either knowing about the other.
 *
 * The list deliberately stays local to the view: its filters live in the URL, it
 * pages, and hoisting all that into a global would give every page a cache to
 * invalidate for no benefit.
 *
 * ── POLLING, AND WHY IT IS GENTLE ────────────────────────────────────────────
 * There is no websocket in this app and adding one would be a poor trade. The
 * roster is two accounts built at boot from env vars (server/lib/users.js), so
 * there is no N to scale away from: the poll below costs one COUNT and one
 * LIMIT 1 per user per minute, and only while someone is looking.
 *
 * The interval is SUSPENDED while the tab is hidden. A dashboard left open on a
 * second monitor overnight would otherwise fire several thousand pointless
 * requests before anyone looked at it again. It also refetches immediately when
 * the tab becomes visible, so coming back shows a current number rather than one
 * up to a minute stale — and raises a toast for anything that landed meanwhile.
 *
 * ── THE TOAST, AND WHY IT KEYS ON AN ID ──────────────────────────────────────
 * The poll returns the newest unread row alongside the count, and a toast fires
 * when that row's id is one this tab has not seen. Keying on the ID rather than
 * on the count is not a detail: marking one read in another tab while a new one
 * arrives leaves the total unchanged, so a count-delta toast would stay silent
 * in exactly the case it exists for.
 *
 * The marker is seeded WITHOUT toasting on the first response after sign-in.
 * Otherwise signing in with a dozen unread alerts would fire a dozen toasts at
 * someone who has not finished reading the page yet.
 *
 * The marker only ever moves FORWARD. `latest` legitimately goes backwards —
 * mark the newest read and the next-newest unread is older — and treating that
 * as a change would re-toast rows the user has already dealt with.
 *
 * Delivery here is best-effort by design: the durable record is the list, and
 * the real-time channel is Telegram via the outbox. A missed toast costs
 * nothing, which is what makes a one-minute poll the right amount of machinery.
 *
 * ── IT FAILS SILENTLY ────────────────────────────────────────────────────────
 * A failed count renders no badge. A notification badge must never be the reason
 * the page looks broken, and Api.get already returns null rather than throwing.
 */

const NotificationsContext = createContext(null);

const POLL_MS = 60000;

export function NotificationsProvider({ children }) {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  // A ref as well as state: the interval closure below must read the CURRENT
  // signed-in value without the effect re-subscribing every time the count
  // changes, which would reset the timer on every poll.
  const signedInRef = useRef(isSignedIn);
  signedInRef.current = isSignedIn;

  // The newest notification id this tab has already accounted for. `null` means
  // "not seeded yet" and is deliberately distinct from 0 ("seeded, and nothing
  // was unread") — collapsing the two would make the first arrival after signing
  // in to an empty inbox seed rather than toast, and silently swallow it.
  const lastSeenIdRef = useRef(null);

  // useNavigate() is NOT referentially stable outside a data router — and this
  // app is BrowserRouter + <Routes>, so it resolves to the unstable variant,
  // whose useCallback lists the current pathname in its dependencies. It is
  // therefore a NEW function after every navigation.
  //
  // Held in a ref for the same reason signedInRef is: announce() feeds refresh()
  // which feeds the polling effect, so letting that identity change on every
  // route change would tear down and rebuild the interval each time — resetting
  // the 60-second countdown and firing an extra request per navigation. Someone
  // clicking around once a minute would poll constantly and never on schedule.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  /**
   * Raise the toast for one row.
   *
   * Two explicit controls rather than a clickable body: "View" goes to the
   * centre, "×" dismisses. A toast whose whole surface is a link has no way to
   * say "seen it, go away" — the only way to get rid of it is to follow it, or
   * to wait — and it gives a keyboard user one target that does the more
   * disruptive of the two things.
   */
  const announce = useCallback((row) => {
    const meta = severityMeta(row.severity);
    // The unread poll only carries type, severity and title — enough to send
    // this one straight to the ads, without putting the path on every toast.
    const openAds = row.type === 'COMMENT_REMINDER';
    toast(
      (t) => (
        <span className='notif-toast'>
          <span className='notif-toast__icon' aria-hidden='true'>{meta.icon}</span>
          <span className='notif-toast__text'>{row.title}</span>
          <span className='notif-toast__actions'>
            <Button
              variant='link'
              className='notif-toast__view'
              onClick={() => {
                toast.dismiss(t.id);
                navigateRef.current(openAds ? COMMENT_REMINDER_PATH : '/notifications');
              }}
            >
              {openAds ? 'Open ads' : 'View'}
            </Button>
            <Button
              variant='bare'
              className='notif-toast__close'
              onClick={() => toast.dismiss(t.id)}
              aria-label='Dismiss notification'
              title='Dismiss'
            >
              ✕
            </Button>
          </span>
        </span>
      ),
      {
        className: `app-toast ${severityToastClass(row.severity)}`.trim(),
        // Ten seconds, against the app-wide default of six. This toast carries
        // something that arrived on its own rather than a reply to something the
        // user just did, so they are not already looking at the spot it appears
        // in — and it now asks them to choose between two buttons, which is not
        // a decision six seconds is enough for if they were mid-sentence
        // somewhere else on the page.
        duration: 10000,
        // Keyed by row id so one notification cannot produce two toasts —
        // react-hot-toast updates a live toast with a matching id rather than
        // stacking a second. This is what makes StrictMode's double-mount in
        // development harmless here.
        id: `notif-${row.id}`,
      },
    );
  }, []);

  const refresh = useCallback(async (signal) => {
    if (!signedInRef.current) return;
    const res = await notificationsService.getUnreadCount({ signal });
    if (signal?.aborted) return;
    // The interval calls refresh() with no signal, so a poll already in flight
    // when the user signs out has nothing to abort it. Without this check it
    // lands afterwards and repaints a badge for a session that is over.
    if (!signedInRef.current) return;
    // `null` is a failed or cancelled request, not "zero unread" — leaving the
    // previous number up is better than flashing the badge away and back.
    if (!res || typeof res.unread !== 'number') return;
    setUnread(res.unread);

    // `latest` is null when nothing is unread — a normal answer, and NOT a
    // reason to move the marker back down. See the header.
    const latestId = res.latest?.id ?? 0;
    const seen = lastSeenIdRef.current;
    if (seen === null) { lastSeenIdRef.current = latestId; return; }
    if (latestId <= seen) return;
    lastSeenIdRef.current = latestId;
    if (res.latest) announce(res.latest);
  }, [announce]);

  useEffect(() => {
    if (!isSignedIn) {
      setUnread(0);
      // Re-seed on the next sign-in. Leaving a previous session's id here would
      // let one account's row numbering decide whether the next is toasted —
      // higher ids silently suppressed, lower ids never firing.
      lastSeenIdRef.current = null;
      return;
    }

    const ctrl = new AbortController();
    refresh(ctrl.signal);

    let timer = null;
    const start = () => { if (!timer) timer = setInterval(() => refresh(), POLL_MS); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') { refresh(); start(); }
      else stop();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      ctrl.abort();
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isSignedIn, refresh]);

  /**
   * Adjust the badge without a round trip.
   *
   * The view already knows what it just did — it marked one row read, or all of
   * them — so the badge can move immediately and the next poll reconciles.
   * Clamped at zero so a double-click cannot drive it negative.
   */
  const adjust = useCallback((delta) => {
    setUnread(n => Math.max(0, n + delta));
  }, []);

  const clear = useCallback(() => setUnread(0), []);

  const value = useMemo(
    () => ({ unread, refresh: () => refresh(), adjust, clear }),
    [unread, refresh, adjust, clear],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used inside a NotificationsProvider');
  return ctx;
}
